import { Injectable, effect, inject, signal } from '@angular/core';
import { AuthTokenService } from '../../services/auth-token.service';
import { ChatDetachService } from './chat-detach.service';
import { clampCornerOffset, type CornerOffset } from './live-chat-drag';

/** V2 route that renders only the chat pop-up, for Admin Portal V1 to show in a frame (Nahid, 7 Oct). */
export const LIVE_CHAT_EMBED_PATH = '/embed/chat';

/** Message tag the V1 host checks (with the frame's origin) before acting on a message. */
export const LIVE_CHAT_EMBED_SOURCE = 'rfi-v2-chat';

/**
 * What the V1 host frame must show:
 * - `closed`: just the chat button (bottom-right corner),
 * - `open`: the pop-up panel and its button,
 * - `full`: the whole screen, while one of the pop-up's dialogs is up (they cover the page),
 * - `hidden`: nothing, because the chat is open in its own window (Nahid, 1 Nov). Only sent to a host that said
 *   `detach: true` in its capabilities; an older host would not know it.
 */
export type LiveChatEmbedState = 'closed' | 'open' | 'full' | 'hidden';

/** Message tag of the V1 host page (checked together with `event.source === window.parent`). */
export const LIVE_CHAT_HOST_SOURCE = 'rfi-v1-host';

/**
 * Where the V1 host has the frame (VE-DRAG), from its `placement` message, in V1 page px. The frame keeps
 * the pop-up's own bottom-right layout; the host moves the frame. `right` / `bottom` are the distances of
 * the frame's bottom-right corner from the V1 viewport's bottom-right corner; `width` x `height` is the
 * frame size for the button / panel (not the full-screen size used for dialogs and while dragging).
 * `viewportWidth` x `viewportHeight` is the area the frame is kept inside (V1's scrollbars excluded) and
 * `fullWidth` x `fullHeight` the full-screen frame's size (100vw x 100vh, scrollbars included).
 */
export interface LiveChatEmbedPlacement extends CornerOffset {
  width: number;
  height: number;
  viewportWidth: number;
  viewportHeight: number;
  fullWidth: number;
  fullHeight: number;
}

/** Root class while the pop-up is drawn away from the frame's corner (frame is full-screen, see styles.scss). */
const SHIFTED_CLASS = 'lc-embed-shifted';

/** Dialogs that cover the whole screen while open. */
const FULL_SCREEN_SELECTORS = [
  '.live-chat__action-modal',
  '.live-chat__incurred-modal',
  '.dne-back',
  '.sce-back',
  '.cce-back',
  '.dep-overlay',
].join(',');

/** Ask V1 for a fresh token this long before it expires, while the pop-up is closed. */
const REFRESH_BEFORE_EXPIRY_SECONDS = 15 * 60;

/**
 * The chat pop-up inside Admin Portal V1. A frame can't let clicks through its transparent parts, so V1
 * keeps the frame exactly as big as what is showing; this service tells it which state that is, and asks
 * for a fresh sign-in token (V1 reloads the frame through its AdminPortalV2/ChatEmbed redirect) before the
 * current one runs out. Messages carry no data, only the state, so they're posted to any parent.
 */
@Injectable({ providedIn: 'root' })
export class LiveChatEmbedService {
  private readonly authToken = inject(AuthTokenService);
  private readonly detach = inject(ChatDetachService);

  /** Framed and on the embed route. Read once at start-up, before the router runs. */
  readonly isEmbed: boolean = LiveChatEmbedService.detectEmbed();

  /**
   * The V1 host can hide the frame (`detach: true` in its capabilities): the chat may then go to its own window, and
   * the frame reports `hidden` while it does. False for an older host, which keeps the chat exactly as it was.
   */
  readonly hostCanDetach = signal(false);

  constructor() {
    // The detached window appeared or went: tell the host (batched with any other change of the same tick).
    effect(() => {
      this.detach.detachedElsewhere();
      this.hostCanDetach();
      if (this.isEmbed && this.observer) this.scheduleState();
    });
  }

  private observer?: MutationObserver;
  private tokenTimer?: ReturnType<typeof setInterval>;
  private stateTimer?: ReturnType<typeof setTimeout>;
  private lastState: LiveChatEmbedState | null = null;
  /** When the last "need a fresh token" went out (asked again after 2 min if nothing happened). */
  private tokenRequestedAt = 0;
  private onActivate?: () => void;

  /**
   * The V1 host can move the frame (it said so with a `capabilities` message and sent a placement). Until
   * then (an older V1 host) the pop-up stays in its corner and the button toggles on press, as before.
   */
  readonly hostCanDrag = signal(false);
  private hostDragAnnounced = false;
  private placement: LiveChatEmbedPlacement | null = null;
  /** The frame position being dragged to (null when not dragging). */
  private dragOffset: CornerOffset | null = null;
  private dragFrom: CornerOffset | null = null;

  private static detectEmbed(): boolean {
    if (typeof window === 'undefined') return false;
    let framed = false;
    try {
      framed = window.self !== window.top;
    } catch {
      framed = true; // cross-origin parent
    }
    return framed && window.location.pathname.toLowerCase().startsWith(LIVE_CHAT_EMBED_PATH);
  }

  /**
   * Starts reporting to the V1 host. Safe to call more than once; does nothing outside the frame.
   * `onActivate` runs when V1 passes on a click on its page (used to ready the alert sound).
   */
  start(onActivate?: () => void): void {
    if (!this.isEmbed || this.observer) return;
    this.onActivate = onActivate;
    window.addEventListener('message', (e: MessageEvent) => {
      if (e.source !== window.parent) return;
      const d = e.data as { source?: string; type?: string } | null;
      if (d?.source !== LIVE_CHAT_HOST_SOURCE) return;
      if (d.type === 'activate') this.onActivate?.();
      else if (d.type === 'capabilities') this.onHostCapabilities(d as { drag?: unknown; detach?: unknown });
      else if (d.type === 'placement') this.onHostPlacement(d as Partial<Record<keyof LiveChatEmbedPlacement, unknown>>);
    });
    // The frame grows to full screen for dialogs / a drag and back: redraw at the right spot each time.
    window.addEventListener('resize', () => this.applyLayoutOffset());
    // Transparent page: only the button / panel / dialogs show over V1 (see styles.scss html.lc-embed).
    document.documentElement.classList.add('lc-embed');

    this.observer = new MutationObserver(() => this.scheduleState());
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.scheduleState();

    this.checkToken();
    this.tokenTimer = setInterval(() => this.checkToken(), 60_000);
  }

  private scheduleState(): void {
    if (this.stateTimer) return;
    // Batches the many DOM changes of one render into a single check.
    this.stateTimer = setTimeout(() => {
      this.stateTimer = undefined;
      this.reportState();
    }, 0);
  }

  private currentState(): LiveChatEmbedState {
    if (this.hostCanDetach() && this.detach.detachedElsewhere()) return 'hidden';
    if (document.querySelector(FULL_SCREEN_SELECTORS)) return 'full';
    if (document.querySelector('.live-chat__panel')) return 'open';
    return 'closed';
  }

  private reportState(): void {
    const state = this.currentState();
    if (state === this.lastState) return;
    this.lastState = state;
    this.post({ type: 'state', state });
    // A token that ran low while the panel was open is refreshed once it's closed again.
    if (state === 'closed' || state === 'hidden') this.checkToken();
  }

  private checkToken(): void {
    // Not mid-drag: the reload would drop it (asked again on the next check).
    if (this.dragFrom) return;
    const left = this.authToken.secondsUntilExpiry();
    const expired = left <= 0;
    // Refreshing reloads the frame, so don't do it under an admin who is working in the pop-up.
    // A hidden frame (chat detached) is as idle as a closed one: nobody works in it, and the detached window needs the token.
    const idleState = this.lastState ?? 'closed';
    const lowAndIdle = left < REFRESH_BEFORE_EXPIRY_SECONDS && (idleState === 'closed' || idleState === 'hidden');
    if ((expired || lowAndIdle) && Date.now() - this.tokenRequestedAt > 120_000) {
      this.tokenRequestedAt = Date.now();
      this.post({ type: 'need-token' });
    }
  }

  // ---- Moving the pop-up (VE-DRAG) ------------------------------------------------------------------
  //
  // The frame can only see the pointer while it is over the frame, so a drag works like this: once the
  // press has moved past the drag threshold, V2 sends `drag-start` and the host makes the frame full-screen
  // (like it does for a dialog); V2 then draws the button / panel at the dragged spot itself (no lag), and
  // sends `drag-end` with the drop position, where the host puts the frame back at its normal size.

  private onHostCapabilities(d: { drag?: unknown; detach?: unknown }): void {
    this.hostCanDetach.set(d.detach === true);
    if (d.drag !== true) return;
    this.hostDragAnnounced = true;
    this.hostCanDrag.set(!!this.placement);
  }

  private onHostPlacement(d: Partial<Record<keyof LiveChatEmbedPlacement, unknown>>): void {
    const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const right = n(d.right);
    const bottom = n(d.bottom);
    const width = n(d.width);
    const height = n(d.height);
    const viewportWidth = n(d.viewportWidth);
    const viewportHeight = n(d.viewportHeight);
    const fullWidth = n(d.fullWidth);
    const fullHeight = n(d.fullHeight);
    if (
      right === null ||
      bottom === null ||
      width === null ||
      height === null ||
      viewportWidth === null ||
      viewportHeight === null ||
      fullWidth === null ||
      fullHeight === null
    ) {
      return;
    }
    this.placement = { right, bottom, width, height, viewportWidth, viewportHeight, fullWidth, fullHeight };
    if (this.hostDragAnnounced) this.hostCanDrag.set(true);
    this.applyLayoutOffset();
  }

  /** The frame is currently full-screen (a dialog is up, or a drag is in progress). */
  private frameIsFullScreen(p: LiveChatEmbedPlacement): boolean {
    return Math.abs(window.innerWidth - p.fullWidth) <= 1 && Math.abs(window.innerHeight - p.fullHeight) <= 1;
  }

  /**
   * A point in this frame (pointer clientX / clientY) in V1 page px. The frame's right / bottom edges are
   * measured from V1's viewport (scrollbars excluded), so its left / top is that minus its size, whether it
   * is full-screen or at its placement.
   */
  toHostPoint(clientX: number, clientY: number): { x: number; y: number } {
    const p = this.placement;
    if (!p) return { x: clientX, y: clientY };
    if (this.frameIsFullScreen(p)) {
      return { x: clientX + (p.viewportWidth - p.fullWidth), y: clientY + (p.viewportHeight - p.fullHeight) };
    }
    return {
      x: clientX + (p.viewportWidth - p.right - p.width),
      y: clientY + (p.viewportHeight - p.bottom - p.height),
    };
  }

  /** The press moved past the threshold: ask V1 for the full-screen frame for the rest of the drag. */
  dragStart(): void {
    const p = this.placement;
    if (!p || !this.hostCanDrag()) return;
    this.dragFrom = { right: p.right, bottom: p.bottom };
    this.dragOffset = { ...this.dragFrom };
    this.post({ type: 'drag-start' });
  }

  /** The pointer moved `dx` / `dy` V1 page px since the press. */
  dragMove(dx: number, dy: number): void {
    const next = this.dragTarget(dx, dy);
    if (!next) return;
    this.dragOffset = next;
    this.applyLayoutOffset();
    this.post({ type: 'drag-move', right: next.right, bottom: next.bottom });
  }

  /** Dropped: V1 puts the frame (normal size) where the pop-up now is. */
  dragEnd(dx: number, dy: number): void {
    const next = this.dragTarget(dx, dy);
    this.dragFrom = null;
    this.dragOffset = null;
    if (!next || !this.placement) return;
    // Draw at the drop spot until V1 has shrunk the frame there (its placement message confirms it).
    this.placement = { ...this.placement, right: next.right, bottom: next.bottom };
    this.applyLayoutOffset();
    this.post({ type: 'drag-end', right: next.right, bottom: next.bottom });
  }

  /** The drag ended outside the browser window (the chat went to its own window): V1 gets the frame back where it was. */
  dragAbort(): void {
    const from = this.dragFrom;
    this.dragFrom = null;
    this.dragOffset = null;
    if (!from) return;
    this.applyLayoutOffset();
    this.post({ type: 'drag-end', right: from.right, bottom: from.bottom });
  }

  private dragTarget(dx: number, dy: number): CornerOffset | null {
    const p = this.placement;
    const from = this.dragFrom;
    if (!p || !from) return null;
    return clampCornerOffset(from.right - dx, from.bottom - dy, p.width, p.height, p.viewportWidth, p.viewportHeight);
  }

  /**
   * While the frame is full-screen, the pop-up's corner layout would put it in the V1 page's corner: shift it
   * to the frame's placement (or the drag position). In the normal-size frame no shift is needed.
   */
  private applyLayoutOffset(): void {
    const root = document.documentElement;
    const p = this.placement;
    const o = this.dragOffset ?? p;
    const shift = !!p && !!o && this.frameIsFullScreen(p) && (o.right !== 0 || o.bottom !== 0);
    if (!shift) {
      root.classList.remove(SHIFTED_CLASS);
      root.style.removeProperty('--lc-embed-dx');
      root.style.removeProperty('--lc-embed-dy');
      return;
    }
    root.style.setProperty('--lc-embed-dx', `${-o!.right}px`);
    root.style.setProperty('--lc-embed-dy', `${-o!.bottom}px`);
    root.classList.add(SHIFTED_CLASS);
  }

  private post(
    message:
      | { type: 'state'; state: LiveChatEmbedState }
      | { type: 'need-token' }
      | { type: 'drag-start' }
      | { type: 'drag-move' | 'drag-end'; right: number; bottom: number },
  ): void {
    try {
      window.parent.postMessage({ source: LIVE_CHAT_EMBED_SOURCE, ...message }, '*');
    } catch {
      /* parent gone */
    }
  }
}
